import React from "react";
import { Pressable, StyleProp, Text } from "react-native";
import { GlobalStyles, Transition } from "../../style/Style";
import { EStyleSheet, useDynamicStyles, ViewStyle } from "react-native-extended-stylesheet-typescript";

export enum ButtonSize {
    MEDIUM = "md",
    LARGE = "lg",
}

export enum ButtonTheme {
    BASE = "dark",
    ACCENTED = "bright",
    CTA = "cta",
}

export type ButtonConfig = {
    buttonText: string;
    size: ButtonSize;
    theme: ButtonTheme;
    customStyle?: StyleProp<ViewStyle>;
    margin?: Margin;
    height?: number;
    width?: number;
    isActive?: boolean;
    disabled?: boolean;
};

export type Margin = {
    left?: number;
    right?: number;
    top?: number;
    bottom?: number;
};

interface ButtonProps {
    config: ButtonConfig;
    testID?: string;
    onPress: () => void;
}

export const RoundedButton: React.FC<ButtonProps> = ({ config, onPress, testID }) => {
    const { backgroundColor, color } = getEnabledButtonColor(config.theme);
    const roundedButtonStyles = useDynamicStyles(() =>
        GenerateStyles(config.size, backgroundColor, color, config.theme, config.margin),
    );
    const activeStyle = config.isActive ? roundedButtonStyles.activeButtonColor : {};
    const stateStyle = config.disabled
        ? roundedButtonStyles.disabledButtonColor
        : roundedButtonStyles.enabledButtonColor;
    const containerWidth = config.width ? { maxWidth: config.width } : {};
    const containerHeight = config.height ? { maxHeight: config.height } : {};

    return (
        <Pressable
            testID={testID}
            style={[
                roundedButtonStyles.button,
                stateStyle,
                containerWidth,
                containerHeight,
                Transition(),
                activeStyle,
                config.customStyle,
            ]}
            disabled={config.disabled}
            onPress={onPress}
        >
            <Text style={roundedButtonStyles.text}>{config.buttonText}</Text>
        </Pressable>
    );
};

const getEnabledButtonColor = (theme: ButtonTheme) => {
    let backgroundColor = "";
    let color = "";

    switch (theme) {
        case ButtonTheme.ACCENTED: {
            backgroundColor = EStyleSheet.value(GlobalStyles.ACCENT_BUTTON_COLOUR);
            color = EStyleSheet.value(GlobalStyles.ACCENT_BUTTON_TEXT);
            break;
        }
        case ButtonTheme.CTA: {
            backgroundColor = EStyleSheet.value(GlobalStyles.CTA_BUTTON_COLOUR);
            color = EStyleSheet.value(GlobalStyles.CTA_BUTTON_TEXT);
            break;
        }
        case ButtonTheme.BASE: {
            backgroundColor = EStyleSheet.value(GlobalStyles.BASE_BUTTON_BACKGROUND);
            color = EStyleSheet.value(GlobalStyles.BASE_BUTTON_TEXT);
            break;
        }
    }
    return { color, backgroundColor };
};

const GenerateStyles = (
    size: ButtonSize,
    enabledBackgroundColor: string,
    enabledTextColor: string,
    theme: ButtonTheme,
    margin?: Margin,
) => {
    return EStyleSheet.create({
        button: {
            flex: 1,
            width: "100%",
            borderRadius: () => EStyleSheet.value(GlobalStyles.BUTTON_RADIUS),
            justifyContent: "center",
            fontFamily: () => EStyleSheet.value(GlobalStyles.HEADER_FONT),
            textAlign: "center",
            height: size === ButtonSize.LARGE ? 50 : 30,
            fontSize: size === ButtonSize.LARGE ? 24 : 18,
            lineHeight: size === ButtonSize.LARGE ? 24 : 19,
            maxWidth: size === ButtonSize.LARGE ? 260 : 120,
            marginLeft: margin?.left !== undefined ? margin.left : 0,
            marginRight: margin?.right !== undefined ? margin.right : 0,
            marginTop: margin?.top !== undefined ? margin.top : 0,
            marginBottom: margin?.bottom !== undefined ? margin.bottom : 10,
        },
        text: {
            fontSize: "inherit",
            color: "inherit",
            lineHeight: "inherit",
            fontFamily: "inherit",
        },
        activeButtonColor: {
            backgroundColor: () => EStyleSheet.value(GlobalStyles.CTA_BUTTON_COLOUR),
            color: () => EStyleSheet.value(GlobalStyles.CTA_BUTTON_TEXT),
        },
        enabledButtonColor: {
            backgroundColor: () => enabledBackgroundColor,
            color: () => enabledTextColor,
        },
        disabledButtonColor: {
            backgroundColor: () => EStyleSheet.value(GlobalStyles.DISABLED_BUTTON),
            color: () => EStyleSheet.value(GlobalStyles.DISABLED_TEXT),
        },
    });
};
